
import { Component, Vue, Ref } from "vue-property-decorator";
import "@fullcalendar/core/vdom"; // solves problem with Vite

import moment, { Moment } from "moment";
import { BusinessHours, WeeklyEvent } from "@/@types/event";
import CalendarModule from "@/store/calendar";
import ReservationModule from "../store/reservation";
import HospitalModule from "../store/hospital";
import MemberService from "@/services/members";
import FacilitiesService from "@/services/facilities";
import { CourseDay, CourseTime, PossibleReservation, ReservationData } from "@/@types/reservation";
import japaneseHolidays from "japanese-holidays";

@Component
export default class Home extends Vue {
  private get currentEvents() {
    return CalendarModule.currentEvents;
  }

  private get selectedCourse() {
    return ReservationModule.selectedCourse;
  }
  private get selectedNumberOfPets() {
    return ReservationModule.selectedNumberOfPets;
  }
  private get businessHours() {
    return CalendarModule.businessHours;
  }
  private get currentDate() {
    return CalendarModule.currentDate;
  }

  private get hospitalId() {
    return HospitalModule.hospitalId;
  }

  // 未来予約枠有無フラグ
  private hasFutureAvailability = false;

  private get numberOfPetsSelectItems() {
    const array = [];
    if (this.selectedCourse) {
      for (let i = 1; i <= this.selectedCourse?.available_count; i++) {
        array.push(i);
      }
    }
    return array;
  }

  private get isPrevButtonDisabled() {
    if (this.loading) return true; // ローディング中は無効化
    const today = moment().startOf("day");
    return moment(this.initialDate).isSameOrBefore(today, "day");
  }

  private onPrevButtonClick() {
    if (this.isPrevButtonDisabled) return; // 無効時はクリックを無視
    this.isUpdatingFromButton = true;
    this.initialDate = moment(this.initialDate).add(-7, "days").toDate();
    this.$nextTick(() => {
      this.isUpdatingFromButton = false;
    });
    this.getPossibleReservations();
  }

  private onNextButtonClick() {
    if (this.loading) return; // ローディング中はクリックを無視
    this.isUpdatingFromButton = true;
    this.initialDate = moment(this.initialDate).add(7, "days").toDate();
    this.$nextTick(() => {
      this.isUpdatingFromButton = false;
    });
    this.getPossibleReservations();
  }

  // 1週間後の日付がpossibleReservationsにない場合trueを返す
  private get isNextButtonDisabled() {
    if (this.loading) return true; // ローディング中は無効化
    return !this.hasFutureAvailability;
  }

  //カレンダーのmax-dateを設定
  private get maxDate(): Date | null {
    const now = new Date();
    // “終了日あり/なし” から計算した候補日をすべて入れる
    const candidates: Date[] = [];
    let startReceptionMonth = 0;
    let startReceptionDate = 0;
    this.selectedCourse?.details.forEach((detail) => {
      const period = detail.period ?? {};
      const hasEnd = !!period.end_year && !!period.end_month && !!period.end_date;

      if (hasEnd) {
        // 明示的な終了日（その日の終端まで考慮）
        const end = new Date(period.end_year, period.end_month - 1, period.end_date, 23, 59, 59, 999);
        // 未来日のみ候補に含める
        if (end.getTime() > now.getTime()) {
          candidates.push(end);
        }
      } else {
        // 「終了日なし」の場合：reception から“終了日”を算出
        detail.days?.forEach((day: any) => {
          day.times?.forEach((time: any) => {
            const rt = time?.reception_time ?? {};
            if (typeof rt.start_month === "number") {
              startReceptionMonth = Math.max(startReceptionMonth, rt.start_month);
            }
            if (typeof rt.start_date === "number") {
              startReceptionDate = Math.max(startReceptionDate, rt.start_date);
            }
          });
        });

        // now を基準にして加算
        const inferred = new Date(now);
        inferred.setHours(23, 59, 59, 999);
        inferred.setMonth(inferred.getMonth() + startReceptionMonth);
        inferred.setDate(inferred.getDate() + startReceptionDate);

        if (inferred.getTime() > now.getTime()) {
          candidates.push(inferred);
        }
      }
    });

    console.log(candidates);
    if (candidates.length === 0) {
      return null; // 候補が無い場合の扱いは要件に応じて
    }

    // 最大（最も遅い）日付を返す
    const max = candidates.reduce((a, b) => (a.getTime() > b.getTime() ? a : b));
    return max;
  }

  private get minDate() {
    // detail.periodのstart_year, start_month, start_dateを比較して一番直近のものをDateで返す
    if (this.selectedCourse?.details) {
      let minDate: Date | null = null;
      this.selectedCourse.details.forEach((detail) => {
        const { start_year, start_month, start_date } = detail.period;
        const detailDate = new Date(start_year, start_month - 1, start_date);
        if (!minDate || detailDate < minDate) {
          minDate = detailDate;
        }
      });
      // minDateが現在より過去だったら現在の日付を返す
      return minDate && minDate < new Date() ? new Date() : minDate;
    }
    return new Date();
  }

  private loading = false;

  private possibleReservations: PossibleReservation[] = [];

  private initialDate = new Date();

  private viewData: any = [];

  private isUpdatingFromButton = false; // ボタンクリックによる更新フラグ

  private changeSelectedNumberOfPets(number: number) {
    ReservationModule.changeSelectedNumberOfPets(number);
    this.getPossibleReservations();
  }

  private createWeeklySchedule(possibleReservations: any) {
    const endDate = new Date(this.initialDate);
    endDate.setDate(endDate.getDate() + 7);

    // 1. this.initialDateから1週間分のデータに絞り込み
    const weeklyReservations = possibleReservations.filter((reservation: ReservationData) => {
      const reservationDate = moment(reservation.datetime);
      const initialDate = moment(this.initialDate)
        .set("hour", 0)
        .set("minute", 0)
        .set("second", 0)
        .set("millisecond", 0);
      return reservationDate.isSameOrAfter(initialDate) && reservationDate.isBefore(moment(endDate));
    });

    // 2. 絞り込んだデータからhh:mmの時間を抽出して配列を作る
    const times = weeklyReservations.map((reservation: ReservationData) => {
      return new Date(reservation.datetime).toTimeString().substring(0, 5);
    });

    // 3. 時間配列の中からダブりを削除し時間が早い順に並び替える
    const uniqueTimes = [...new Set(times)].sort();
    // 4. テーブル構造になるよう整形する[[時間,予約枠, 予約枠...],[時間,予約枠, 予約枠...]]
    const formattedData = uniqueTimes.map((time) => {
      const dailyData = new Array(7).fill(null).map((_, index) => {
        const day = new Date(this.initialDate);
        if (index !== 0) {
          day.setDate(day.getDate() + index);
        }
        const dayStr = day
          .toLocaleDateString("ja-JP", { year: "numeric", month: "2-digit", day: "2-digit" })
          .split("/")
          .join("-");
        const reservationForDayAndTime = weeklyReservations.find((reservation: ReservationData) => {
          const reservationDate = new Date(reservation.datetime)
            .toLocaleDateString("ja-JP", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
            .split("/")
            .join("-");
          const reservationTime = new Date(reservation.datetime).toTimeString().substring(0, 5);
          return reservationDate == dayStr && reservationTime == time;
        });
        return reservationForDayAndTime
          ? {
              reserved_count: reservationForDayAndTime.reserved_count,
              remained_count: reservationForDayAndTime.remained_count,
              available_count: reservationForDayAndTime.available_count,
              datetime: reservationForDayAndTime.datetime,
            }
          : { reserved_count: 0, remained_count: 0, available_count: 0 };
      });
      return [time, ...dailyData];
    });
    //4/16 (火)の形式で日付の配列を作る["4/16 (火)", "4/17 (水)""]
    const days = new Array(8).fill(null).map((_, index) => {
      if (index === 0) return "";
      const day = new Date(this.initialDate);
      day.setDate(day.getDate() + (index - 1));
      return `${day.getMonth() + 1}/${day.getDate()} (${["日", "月", "火", "水", "木", "金", "土"][day.getDay()]})`;
    });

    this.viewData = [days, ...formattedData];
  }

  /**
   * 予約スロットが受付時間内かどうかを判定
   * @param reservationDatetime 予約日時（ISO形式）
   * @returns 受付時間内の場合 true、外の場合 false
   */
  private isWithinReceptionTime(reservationDatetime: string): boolean {
    const now = moment();
    const reservationMoment = moment(reservationDatetime);

    // selectedCourse の details から reception_time 情報を取得
    if (!this.selectedCourse?.details) {
      return true; // 詳細情報がない場合は受付時間内と判定
    }

    // 予約日時の曜日を取得 (0=日, 1=月, ..., 6=土)
    const reservationDayOfWeek = reservationMoment.day();
    const reservationHour = reservationMoment.hour();
    const reservationMinute = reservationMoment.minute();
    // 予約日が祝日かどうか判定
    const isReservationHoliday = this.checkHoliday(reservationMoment.toDate());

    for (const detail of this.selectedCourse.details) {
      if (!detail.days) continue;

      for (const day of detail.days) {
        const availableDays = day.available_days || [];

        // 曜日マッピング
        const dayNameToDayOfWeek: { [key: string]: number } = {
          Sun: 0,
          Mon: 1,
          Tue: 2,
          Wed: 3,
          Thu: 4,
          Fri: 5,
          Sat: 6,
        };

        const dayOfWeekNumbers = availableDays.map((d: string) => dayNameToDayOfWeek[d] ?? -1);

        // 予約日時の曜日が available_days に含まれるか、
        // または予約日が祝日かつ available_days に "Holiday" が含まれるか
        const weekdayMatches = dayOfWeekNumbers.includes(reservationDayOfWeek);
        const holidayMatches = isReservationHoliday && availableDays.includes("Holiday");
        if (!weekdayMatches && !holidayMatches) {
          continue;
        }

        // ✅ 修正: スロット時刻が属する時間帯を特定
        for (const time of day.times || []) {
          const startHour = time.on_time.start_hour;
          const startMinute = time.on_time.start_minute;
          const endHour = time.on_time.end_hour;
          const endMinute = time.on_time.end_minute;

          // スロット時刻がこの時間帯に含まれるか確認
          const slotTimeInMinutes = reservationHour * 60 + reservationMinute;
          const timeStartInMinutes = startHour * 60 + startMinute;
          const timeEndInMinutes = endHour * 60 + endMinute;

          // 時間帯に含まれない場合はスキップ
          if (slotTimeInMinutes < timeStartInMinutes || slotTimeInMinutes >= timeEndInMinutes) {
            continue;
          }

          // スロットが属する時間帯の reception_time のみをチェック
          const receptionTime = time.reception_time || {};
          const endMonth = receptionTime.end_month || 0;
          const endDate = receptionTime.end_date || 0;
          const endHourReception = receptionTime.end_hour || 0;

          // 予約日時から月・日を逆算して受付終了時刻を計算
          const receptionEndDateTime = moment(reservationDatetime)
            .add(-endMonth, "months")
            .add(-endDate, "days")
            .set("hour", endHourReception)
            .set("minute", 0)
            .set("second", 0)
            .set("millisecond", 0);

          // この時間帯の受付時間内なら true
          if (now.isSameOrBefore(receptionEndDateTime)) {
            return true;
          }
        }
      }
    }

    return false; // どの時間帯にも該当しない or 受付時間外
  }

  /**
   * possibleReservations をフロント側で受付時間チェックしてフィルタ
   * 受付時間外の場合は remained_count を 0 に設定（表示上「×」になる）
   */
  private filterByReceptionTime(reservations: PossibleReservation[]): PossibleReservation[] {
    return reservations.map((reservation) => {
      if (!this.isWithinReceptionTime(reservation.datetime)) {
        // 受付時間外の場合、remained_count を 0 に設定
        return {
          ...reservation,
          remained_count: 0,
        };
      }
      return reservation;
    });
  }

  private async checkFutureAvailability() {
    if (!this.selectedCourse) return;
    // カレンダー最終日： initialDate + 6日
    const lastDisplayedDate = moment(this.initialDate).add(6, "days").format("YYYY-MM-DD");
    try {
      if (!this.selectedCourse || !this.selectedCourse.id) {
        // 英語でログ出力
        console.error("selectedCourseまたはそのIDが未定義です");
        return;
      }
      const res = await MemberService.hasFutureAvailability(this.hospitalId, this.selectedCourse.id, lastDisplayedDate);
      // this.hasFutureAvailability = res.data === true;
      this.hasFutureAvailability = res.data.data[0].has_availability === true;
    } catch (e) {
      console.error("未来予約枠有無チェック失敗", e);
      this.hasFutureAvailability = false;
    }
  }

  private async getPossibleReservations() {
    try {
      this.loading = true;

      const calendarStartDate = moment(this.initialDate);
      const calendarEndDate = moment(this.initialDate).add(7, "days");

      const startYear = calendarStartDate.get("year").toString();
      const startMonth = String(calendarStartDate.get("month") + 1);

      // 28日後の日付から年月を取得（21日後ではなく28日後で余裕を持たせる）
      const endDate = calendarEndDate.clone().add(21, "days");
      const endYear = String(endDate.get("year"));
      const endMonth = String(endDate.get("month") + 1);

      if (this.selectedCourse && this.selectedCourse.id) {
        const promises = [
          MemberService.getPossibleReservations(this.hospitalId, this.selectedCourse.id, startYear, startMonth),
        ];
        if (endMonth !== startMonth) {
          promises.push(
            MemberService.getPossibleReservations(this.hospitalId, this.selectedCourse.id, endYear, endMonth)
          );
        }
        const results = await Promise.all(promises);
        // マージして日時順にソート
        const merged = results
          .flatMap((r) => r.data.data ?? [])
          .sort((a, b) => moment(a.datetime).valueOf() - moment(b.datetime).valueOf());

        // フロント側で受付時間をフィルタ（複数時間帯で一部が受付時間外の場合に対応）
        this.possibleReservations = this.filterByReceptionTime(merged);

        this.createWeeklySchedule(this.possibleReservations);

        // 未来予約枠有無チェック
        await this.checkFutureAvailability();
      }
    } catch (error: any) {
      console.error("予約可能枠の取得に失敗しました:", error);
    } finally {
      this.loading = false;
    }
  }

  private selectDate(start: string) {
    if (this.$route.name === "MyPageReservationEdit") {
      this.$emit("select-date", start);
    } else {
      ReservationModule.changeSelectedDate(start);
      this.$router.push({ name: "HospitalReservation" });
    }
  }
  private start = ""; //現在表示している日時のスタート
  private end = ""; //現在表示している日時のエンド

  private isCalendarViewToday() {
    const today = moment(new Date()).format("YYYY/MM/DD");
    const calendarViewDay = this.initialDate ? moment(this.initialDate).format("YYYY/MM/DD") : "";
    return today == calendarViewDay;
  }

  private changePrevButtonDisabled(element: Element) {
    if (this.isCalendarViewToday()) {
      element.setAttribute("disabled", "true");
    } else {
      element.removeAttribute("disabled");
    }
  }

  private changeInitialDate() {
    // 既にDateオブジェクトの場合は新しくmomentオブジェクトを作成しない
    if (this.initialDate instanceof Date) {
      const date = moment(this.initialDate);
      date.set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      this.initialDate = date.toDate();
      CalendarModule.changeCurrentDate(date.format("YYYY-MM-DD"));
    }
  }

  private checkHoliday(date: Date) {
    return japaneseHolidays.isHoliday(date);
  }

  private async mounted() {
    await this.getCourse();
    this.setInitialNumberOfPets(); // どうぶつ数の初期値設定
    this.getPossibleReservations();
  }

  private setInitialNumberOfPets() {
    const current = ReservationModule.selectedNumberOfPets;
    const formPetsCount = ReservationModule.reservationInfo.pets.length;
    const maxCount = this.selectedCourse?.available_count ?? 1;

    // 戻る時は current に値が入っているので初期化しない
    if (current !== 1) {
      return;
    }

    if (formPetsCount > 0) {
      // コース変更時 → 入力済みどうぶつ数orコースの最大どうぶつ数を初期値にする
      const initialCount = Math.min(formPetsCount, maxCount);
      ReservationModule.changeSelectedNumberOfPets(initialCount);
    } else {
      // 新規予約時 → 1
      ReservationModule.changeSelectedNumberOfPets(1);
      // 最大数を初期値にする場合：
      // ReservationModule.changeSelectedNumberOfPets(maxCount);
    }
  }

  private async getCourse() {
    if (!this.selectedCourse) return;
    const course = await FacilitiesService.getCourseDetail(this.hospitalId, this.selectedCourse.id);
    ReservationModule.changeSelectedCourse(course.data.data[0]);
  }
  private convertDays(days: string[]) {
    const convertedDays = days.map((value) => {
      if (value === "Sun") {
        return 0;
      } else if (value === "Mon") {
        return 1;
      } else if (value === "Tue") {
        return 2;
      } else if (value === "Wed") {
        return 3;
      } else if (value === "Thu") {
        return 4;
      } else if (value === "Fri") {
        return 5;
      } else if (value === "Sat") {
        return 6;
      } else {
        return 99;
      }
    });
    return convertedDays;
  }
  private businessHourStringFactory(hour: number, minute: number, duration?: number) {
    let hours = moment().set("hour", hour).set("minute", minute).format("HH:mm");
    if (duration && hour === 0 && minute === 0) {
      hours = moment().set("hour", hour).set("minute", minute).add(-1, "minute").format("HH:mm");
    } else if (duration) {
      hours = moment().set("hour", hour).set("minute", minute).add(duration, "minute").format("HH:mm");
      if (hours.slice(0, 2) === "00" && hours.slice(3, 5) === "00") {
        hours = "23:59";
      }
    }
    return hours;
  }
  private endBusinessHourStringFactory(start_time: string, end_time: string) {
    const duration = this.selectedCourse?.duration_minute;
    if (!duration) {
      throw new Error("Duration is not defined");
    }

    // start_time = "09:15"
    // end_time = "17:00"
    // duration = 90
    // start_timeにdurationを足していき、end_timeを超えた時、その1つ前の時間を返す

    const [startHour, startMinute] = start_time.split(":").map(Number);
    const [endHour, endMinute] = end_time.split(":").map(Number);

    let startTime = moment().set("hour", startHour).set("minute", startMinute);
    const endTime = moment().set("hour", endHour).set("minute", endMinute);

    let previousTime = startTime.clone(); // 1つ前の時間を保存

    // startTimeがendTimeを超えるまでループ
    while (startTime.isSameOrBefore(endTime)) {
      previousTime = startTime.clone();
      startTime = startTime.add(duration, "minutes");
    }

    // 1つ前の時間をHH:MM形式で返す
    return previousTime.format("HH:mm");
  }
  private businessHoursArrayFactory() {
    const businessHoursArray: BusinessHours[] = [];

    this.selectedCourse?.details.forEach((detail) => {
      detail.days?.forEach((courseDay: CourseDay) => {
        // 曜日のリストをstring→numberへ
        const daysOfWeek = this.convertDays(courseDay.available_days);
        // 終日休診じゃない場合
        if (!courseDay.is_no_medical_all_day) {
          courseDay.times.forEach((time) => {
            const startTime = this.businessHourStringFactory(time.on_time.start_hour, time.on_time.start_minute);
            let endTime = this.businessHourStringFactory(time.on_time.end_hour, time.on_time.end_minute);
            endTime = this.endBusinessHourStringFactory(startTime, endTime);
            if (time.on_time.end_hour == 24) {
              endTime = "24:00";
            }
            businessHoursArray.push({
              daysOfWeek,
              startTime,
              endTime,
            });
          });
        }
      });
    });
    CalendarModule.changeBusinessHours(businessHoursArray);
  }

  private getLabelColor(label: string) {
    if (label === "一般診療") {
      return "rgba(85, 171, 53, 0.6)";
    } else if (label === "予防接種") {
      return "rgba(53, 62, 171, 0.6)";
    }
  }

  private weeklyFactory(businessHours: BusinessHours[]) {
    const weekly: WeeklyEvent[] = [];
    const now = new Date();
    let startHour = 0;
    let startMinute = 0;

    //仕事開始時間
    businessHours.sort((a, b) => {
      return Number(a.startTime.slice(0, 2)) < Number(b.startTime.slice(0, 2)) ? -1 : 1;
    });
    businessHours.forEach((businessHour) => {
      let weeklyStart = moment(this.start)
        .hour(Number(businessHour.startTime.slice(0, 2)))
        .minute(Number(businessHour.startTime.slice(3, 5)))
        .format();
      startHour = Number(businessHour.startTime.slice(0, 2));
      startMinute = Number(businessHour.startTime.slice(3, 5));
      // 仕事終了時間
      // businessHours.sort((a: BusinessHours, b: BusinessHours) => {
      //   if (a.endTime.slice(0, 2) > b.endTime.slice(0, 2)) {
      //     return 1;
      //   } else {
      //     return -1;
      //   }
      // });
      let endTime = moment(weeklyStart)
        .hour(Number(businessHour.endTime.slice(0, 2)))
        .minute(Number(businessHour.endTime.slice(3, 5)))
        .format();
      // 予約受付枠のスタート日時定義
      // 同じ曜日設定の時間帯リスト
      // const dayTimes = businessHours.filter(
      //   (value) =>
      //     businessHour.daysOfWeek.length === [...new Set([...value.daysOfWeek, ...businessHour.daysOfWeek])].length
      // );
      // 表示しているカレンダーのスタート日時からエンド日時までループ
      for (let i = 1; moment(weeklyStart).isBefore(moment(this.end)); i++) {
        // 予約数
        const event: WeeklyEvent = {
          title: 0,
          start: weeklyStart,
          end: moment(weeklyStart)
            .add(this.selectedCourse!.duration_minute / 60, "hours")
            .format(),
        };
        const eventDay = moment(event.start).get("day");
        if (
          businessHour.daysOfWeek.includes(eventDay) ||
          (businessHour.daysOfWeek.includes(99) && this.checkHoliday(new Date(event.start)))
        ) {
          //予約可能枠リストから同日時の予約枠取得
          const findPossibleReservation = this.possibleReservations.find((reservation) =>
            moment(reservation.datetime).isSame(weeklyStart)
          );
          if (findPossibleReservation) {
            event.title = findPossibleReservation.remained_count;
          }
          if (this.selectedCourse?.details[0].special_times) {
            const findSpecialTime = this.selectedCourse?.details[0].special_times.find(
              (item) =>
                moment()
                  .set("year", item.year)
                  .set("month", item.month - 1)
                  .set("date", item.date)
                  .set("hour", item.start_hour)
                  .set("minute", item.start_minute)
                  .set("second", 0)
                  .format() === weeklyStart
            );
            if (findSpecialTime && findPossibleReservation) event.title = findPossibleReservation.remained_count;
          }
          if (moment(event.start).isBefore(now)) {
            event.title = 0;
          }
          weekly.push(event);
        }
        if (event.end >= endTime) {
          weeklyStart = moment(weeklyStart).add(1, "days").hour(startHour).minute(startMinute).format();
          endTime = moment(endTime).add(1, "days").format();
        } else {
          weeklyStart = event.end;
        }
      }
    });
    let nonBusinessWeekly: WeeklyEvent[] = [];
    nonBusinessWeekly = this.addNonBusinessHourTime(this.businessHours);
    const filteredEvents = this.filterEvents([...weekly, ...nonBusinessWeekly]);
    // weeklyの中にstartが同じ時間のものがあればtitleが少ない方を削除
    return filteredEvents;
  }

  private addNonBusinessHourTime(businessHours: BusinessHours[]) {
    const nonBusinessWeekly: WeeklyEvent[] = [];
    let startHour = 0;
    let startMinute = 0;

    //仕事開始時間
    businessHours.sort((a, b) => {
      return Number(a.startTime.slice(0, 2)) < Number(b.startTime.slice(0, 2)) ? -1 : 1;
    });
    businessHours.forEach((businessHour) => {
      let weeklyStart = moment(this.start)
        .hour(Number(businessHour.startTime.slice(0, 2)))
        .minute(Number(businessHour.startTime.slice(3, 5)))
        .format();
      startHour = Number(businessHour.startTime.slice(0, 2));
      startMinute = Number(businessHour.startTime.slice(3, 5));
      let endTime = moment(weeklyStart)
        .hour(Number(businessHour.endTime.slice(0, 2)))
        .minute(Number(businessHour.endTime.slice(3, 5)))
        .format();
      // 表示しているカレンダーのスタート日時からエンド日時までループ
      for (let i = 1; moment(weeklyStart).isBefore(moment(this.end)); i++) {
        // 予約数
        const event: WeeklyEvent = {
          title: 0,
          start: weeklyStart,
          end: moment(weeklyStart)
            .add(this.selectedCourse!.duration_minute / 60, "hours")
            .format(),
        };
        // 同じ時間帯に予約枠がなければpush
        if (!this.currentEvents.find((item) => moment(item.start).isSame(event.start))) {
          nonBusinessWeekly.push(event);
        }
        weeklyStart = event.end;
      }
    });
    return nonBusinessWeekly;
  }

  private filterEvents(events: WeeklyEvent[]) {
    const filteredEvents: WeeklyEvent[] = [];
    const groupedEvents: any = {};

    // グループ化された同じ"start"日時のイベントを作成
    events.forEach((event) => {
      const start = event.start;
      if (groupedEvents[start]) {
        groupedEvents[start].push(event);
      } else {
        groupedEvents[start] = [event];
      }
    });

    // 同じ"start"日時のイベントの中で"tile"が一番大きいものを選択
    Object.values(groupedEvents).forEach((group: any) => {
      if (group.length > 1) {
        const maxTitleEvent = group.reduce((maxEvent: WeeklyEvent, currentEvent: WeeklyEvent) => {
          return currentEvent.title > maxEvent.title ? currentEvent : maxEvent;
        });
        filteredEvents.push(maxTitleEvent);
      } else {
        filteredEvents.push(group[0]);
      }
    });

    return filteredEvents;
  }

  private get hasAvailableSlots(): boolean {
    // 配列の長さが1以下（ヘッダーのみ）の場合はfalse
    if (this.viewData.length <= 1) return false;

    // ヘッダー行を除いた各行をチェック
    return this.viewData.slice(1).some((row) => {
      // 時間列（0番目）を除いた予約枠をチェック
      return row.slice(1).some((cell) => {
        return cell.available_count > 0;
      });
    });
  }

  private get filteredViewData() {
    return this.viewData.slice(1);
  }

  private onDateUpdate(newDate: Date | null) {
    // ボタンクリックによる更新の場合はスキップ
    if (this.isUpdatingFromButton) {
      return;
    }

    // nullの場合は現在の値を維持
    if (!newDate) {
      this.$nextTick(() => {
        this.initialDate = this.initialDate;
      });
      return;
    }

    const date = moment(newDate);
    date.set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
    this.initialDate = date.toDate();
    this.getPossibleReservations();
  }
}
